export interface StockData {
  company: string;
  symbol: string;
  picked: string;
  return: string;
  sector: string;
  rating: string;
  holdingPercent: string;
  high52w?: number | null;
  low52w?: number | null;
  currentPrice?: number | null;
  pickedPrice?: number | null;
}

export class GoogleSheetsService {
  private SHEET_ID: string = '1NH5JOSkN5fbnbUwaUEaAN80Qcfaki03NpiT8R6EOjj0';

  constructor(sheetUrl?: string) {
    if (sheetUrl) {
      this.setSheetUrl(sheetUrl);
    }
  }

  /**
   * Extract Sheet ID from Google Sheets URL
   */
  private extractSheetId(url: string): string | null {
    // Match various Google Sheets URL formats:
    // https://docs.google.com/spreadsheets/d/{SHEET_ID}/...
    const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    return match ? match[1] : null;
  }

  /**
   * Set the Google Sheets URL and extract the sheet ID
   */
  setSheetUrl(url: string): void {
    const sheetId = this.extractSheetId(url);
    if (sheetId) {
      this.SHEET_ID = sheetId;
      console.log('Google Sheets ID updated:', sheetId);
    } else {
      console.warn('Could not extract sheet ID from URL:', url);
    }
  }

  /**
   * Get the current sheet ID
   */
  getSheetId(): string {
    return this.SHEET_ID;
  }

  // วิธีใหม่: ใช้ CSV Export format
  async fetchData(): Promise<StockData[]> {
    try {
      // ลองใช้วิธีที่แตกต่างกันในการดึง CSV
      const methods = [
        // วิธีที่ 1: CSV export
        () => this.fetchCSVExport(),
        // วิธีที่ 2: gviz format (เดิม)
        () => this.fetchGvizCSV(),
        // วิธีที่ 3: Published CSV
        () => this.fetchPublishedCSV()
      ];
      
      for (let i = 0; i < methods.length; i++) {
        try {
          console.log(`Trying method ${i + 1}...`);
          const data = await methods[i]();
          if (data && data.length > 0) {
            console.log(`Method ${i + 1} successful, got ${data.length} records`);
            return data;
          }
        } catch (error) {
          console.warn(`Method ${i + 1} failed:`, (error as Error).message);
        }
      }
      
      throw new Error('All fetch methods failed');
    } catch (error) {
      console.error('Error fetching Google Sheets data:', error);
      throw error;
    }
  }
  
  private async fetchCSVExport(): Promise<StockData[]> {
    const csvExportUrl = `https://docs.google.com/spreadsheets/d/${this.SHEET_ID}/export?format=csv&gid=0`;
    
    const response = await fetch(csvExportUrl, {
      headers: {
        'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36',
        'Accept': 'text/csv,application/csv,text/plain',
      },
      redirect: 'follow'
    });
    
    if (!response.ok) {
      throw new Error(`CSV export HTTP ${response.status}: ${response.statusText}`);
    }
    
    const csvText = await response.text();
    
    // ตรวจสอบว่าเป็น HTML redirect หรือไม่
    if (csvText.includes('<HTML>') || csvText.includes('<html>')) {
      throw new Error('Got HTML redirect instead of CSV');
    }
    
    return this.parseCSVExport(csvText);
  }
  
  private async fetchGvizCSV(): Promise<StockData[]> {
    const range = 'A1:G1000'; // เริ่มจากแถวแรกเพื่อหา header
    const url = `https://docs.google.com/spreadsheets/d/${this.SHEET_ID}/gviz/tq?tqx=out:csv&range=${range}`;
    
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'Mozilla/5.0 (compatible; InvestmentApp/1.0)',
        'Accept': 'text/csv,application/csv,text/plain'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Gviz CSV HTTP ${response.status}: ${response.statusText}`);
    }
    
    const csvText = await response.text();
    return this.parseCSVExport(csvText);
  }
  
  private async fetchPublishedCSV(): Promise<StockData[]> {
    // ต้อง publish sheet เป็น web ก่อน
    const publishedUrl = `https://docs.google.com/spreadsheets/d/e/2PACX-1vQ${this.SHEET_ID.substring(10)}/pub?output=csv`;
    
    const response = await fetch(publishedUrl, {
      headers: {
        'User-Agent': 'Mozilla/5.0 (compatible; InvestmentApp/1.0)',
        'Accept': 'text/csv,application/csv,text/plain'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Published CSV HTTP ${response.status}: ${response.statusText}`);
    }
    
    const csvText = await response.text();
    return this.parseCSVExport(csvText);
  }

  // วิธีเดิม (backup) - เรียกทีละคอลัมน์
  async fetchDataOldWay(): Promise<StockData[]> {
    try {
      const ranges = [
        'A7:A100', 'B7:B100', 'C7:C100', 'D7:D100', 
        'E7:E100', 'F7:F100', 'G7:G100'
      ];
      
      const promises = ranges.map(range => this.fetchRange(range));
      const results = await Promise.all(promises);
      
      return this.combineData(results);
    } catch (error) {
      console.error('Error fetching Google Sheets data (old way):', error);
      throw error;
    }
  }

  private async fetchRange(range: string): Promise<string[]> {
    const url = `https://docs.google.com/spreadsheets/d/${this.SHEET_ID}/gviz/tq?tqx=out:csv&range=${range}`;
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch range ${range}: ${response.statusText}`);
    }
    
    const csvText = await response.text();
    return this.parseCsv(csvText);
  }

  private parseCsv(csvText: string): string[] {
    return csvText
      .split('\n')
      .map(line => line.replace(/"/g, '').trim())
      .filter(line => line.length > 0);
  }

  private combineData(results: string[][]): StockData[] {
    const [companies, symbols, picked, returns, sectors, ratings, holdingPercents] = results;
    
    const maxLength = Math.max(...results.map(arr => arr.length));
    const stockData: StockData[] = [];

    for (let i = 0; i < maxLength; i++) {
      stockData.push({
        company: companies[i] || '',
        symbol: symbols[i] || '',
        picked: picked[i] || '',
        return: returns[i] || '',
        sector: sectors[i] || '',
        rating: ratings[i] || '',
        holdingPercent: holdingPercents[i] || ''
      });
    }

    return stockData.filter(stock => stock.company || stock.symbol);
  }

  // ฟังก์ชัน parsing ใหม่ที่ดีกว่า
  private parseImprovedCSV(csvText: string): StockData[] {
    // Clean: remove all newlines inside quoted fields, then split by line
    const cleaned = csvText.replace(/"([^"]*)"/g, (m) => m.replace(/\n/g, ' '));
    const lines = cleaned.split('\n').filter(line => line.trim());
    
    return lines.map(line => {
      const columns = this.parseCSVLine(line);
      
      let pickedDate = columns[2] || '';
      // Check for Google's "Date(year,month,day)" format from CSV export
      const dateMatch = pickedDate.match(/Date\((\d{4}),(\d{1,2}),(\d{1,2})\)/);
      if (dateMatch) {
        const year = parseInt(dateMatch[1]);
        const month = parseInt(dateMatch[2]) + 1; // Month is 0-indexed in this format
        const day = parseInt(dateMatch[3]);
        // Format to mm/dd/yyyy which is a more standard format
        pickedDate = `${month}/${day}/${year}`;
      }

      return {
        company: columns[0] || '',
        symbol: columns[1] || '',
        picked: pickedDate,
        return: columns[3] || '',
        sector: columns[4] || '',
        rating: columns[5] || '',
        holdingPercent: columns[6] || ''
      };
    }).filter(stock => stock.company && stock.company.trim() !== '' && stock.company !== 'Company');
  }

  // ฟังก์ชันใหม่สำหรับ parse CSV Export
  private parseCSVExport(csvText: string): StockData[] {
    const lines = csvText.split('\n').filter(line => line.trim());
    console.log(`Total lines in CSV: ${lines.length}`);
    
    // หา header row ที่มี "Company", "Symbol" etc. 
    let headerIndex = -1;
    for (let i = 0; i < Math.min(20, lines.length); i++) {
      const columns = this.parseCSVLine(lines[i]);
      // ตรวจสอบว่าคอลัมน์แรกและคอลัมน์ที่สองมี "Company" และ "Symbol"
      if (columns.length >= 2 && 
          columns[0].toLowerCase().includes('company') && 
          columns[1].toLowerCase().includes('symbol')) {
        headerIndex = i;
        console.log(`Found header at line ${i + 1}: ${JSON.stringify(columns.slice(0, 7))}`);
        break;
      }
    }
    
    if (headerIndex === -1) {
      console.log('Header not found, searching for data patterns...');
      // ค้นหา pattern ข้อมูล: บรรทัดที่มี symbol pattern (2-5 ตัวอักษร)
      for (let i = 0; i < Math.min(30, lines.length); i++) {
        const columns = this.parseCSVLine(lines[i]);
        if (columns.length >= 2 && 
            columns[1] && 
            /^[A-Z]{2,5}$/.test(columns[1].trim())) {
          headerIndex = i - 1; // ข้อมูลเริ่มต้น
          console.log(`Found data pattern at line ${i + 1}, assuming header at ${i}: ${JSON.stringify(columns.slice(0, 7))}`);
          break;
        }
      }
    }
    
    if (headerIndex === -1) {
      headerIndex = 6; // fallback to row 7
      console.log('Using fallback header index 6');
    }
    
    const dataLines = lines.slice(headerIndex + 1);
    const parsedData: StockData[] = [];
    
    for (let i = 0; i < dataLines.length; i++) {
      const line = dataLines[i];
      if (!line.trim()) continue;
      
      const columns = this.parseCSVLine(line);
      
      // ตรวจสอบว่ามีข้อมูลครบและ symbol เป็น pattern ที่ถูกต้อง
      if (columns.length >= 6 && 
          columns[0] && columns[0].trim() && 
          columns[1] && /^[A-Z\.]{1,8}$/.test(columns[1].trim().toUpperCase())) {
        
        // ทำความสะอาดข้อมูลวันที่
        let pickedDate = columns[2] || '';
        pickedDate = this.cleanDateString(pickedDate);
        
        // ข้อมูล return อาจอยู่ในคอลัมน์ 3 หรือ 4 ขึ้นอยู่กับ layout
        let returnValue = '';
        let sector = '';
        let rating = '';
        let holdingPercent = '';
        
        // ตรวจสอบคอลัมน์ที่มี % (return value)
        for (let j = 2; j < columns.length; j++) {
          const col = columns[j]?.trim() || '';
          if (col && col.includes('%') && /[\d\-+]/.test(col)) {
            if (!returnValue && (col.includes('.') || col.length > 2)) {
              returnValue = col;
            } else if (!holdingPercent) {
              holdingPercent = col;
            }
          }
        }
        
        // หา sector และ rating
        for (let j = 3; j < Math.min(columns.length, 7); j++) {
          const col = columns[j]?.trim() || '';
          if (col && !col.includes('%')) {
            if (!sector && (col.includes('Technology') || col.includes('Consumer') || 
                           col.includes('Industrial') || col.includes('Financial') ||
                           col.includes('Communication') || col.includes('Materials') ||
                           col.includes('Health') || col.includes('Energy') ||
                           col.includes('Utilities') || col.includes('Real Estate'))) {
              sector = col;
            } else if (!rating && (col.includes('Buy') || col.includes('Hold') || col.includes('Strong'))) {
              rating = col;
            }
          }
        }
        
        const stockData: StockData = {
          company: columns[0].trim(),
          symbol: columns[1].trim().toUpperCase(),
          picked: pickedDate,
          return: returnValue,
          sector: sector,
          rating: rating,
          holdingPercent: holdingPercent
        };
        
        // ตรวจสอบว่าไม่ใช่ header row และมีข้อมูลที่สมเหตุสมผล
        if (stockData.company !== 'Company' && 
            stockData.symbol !== 'Symbol' && 
            stockData.company.length > 3) {
          parsedData.push(stockData);
          console.log(`Row ${parsedData.length}: ${stockData.company} (${stockData.symbol}) - ${stockData.picked} - ${stockData.return}`);
        }
      }
    }
    
    console.log(`Parsed ${parsedData.length} valid stock records from CSV`);
    return parsedData;
  }
  
  // ฟังก์ชันทำความสะอาดข้อมูลวันที่
  private cleanDateString(dateStr: string): string {
    if (!dateStr || dateStr.trim() === '') return '';
    
    let cleaned = dateStr.trim().replace(/"/g, ''); // ลบ quotes
    
    // จัดการกับ Google Sheets Date function
    const dateMatch = cleaned.match(/Date\((\d{4}),(\d{1,2}),(\d{1,2})\)/);
    if (dateMatch) {
      const year = parseInt(dateMatch[1]);
      const month = parseInt(dateMatch[2]) + 1; // Google uses 0-indexed months
      const day = parseInt(dateMatch[3]);
      cleaned = `${month}/${day}/${year}`;
    }
    
    // แปลง mm/dd/yyyy เป็น mm/dd/yyyy (standardize format)
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(cleaned)) {
      const [month, day, year] = cleaned.split('/');
      return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
    }
    
    return cleaned;
  }

  // ปรับปรุง CSV parsing ให้จัดการ quotes ได้ดีขึ้น
  private parseCSVLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  }

  // ฟังก์ชันทดลองใช้ proxy (กรณีมีปัญหา CORS)
  async fetchDataWithProxy(): Promise<StockData[]> {
    try {
      const proxyUrl = 'https://api.allorigins.win/get?url=';
      const sheetUrl = `https://docs.google.com/spreadsheets/d/${this.SHEET_ID}/gviz/tq?tqx=out:csv&range=A7:G1000`;
      const url = proxyUrl + encodeURIComponent(sheetUrl);
      
      console.log(`Fetching via proxy: ${url}`);
      
      const response = await fetch(url);
      const data = await response.json();
      const csvText = data.contents;
      
      return this.parseImprovedCSV(csvText);
    } catch (error) {
      console.error('Proxy fetch error:', error);
      throw error;
    }
  }
}